import { DownloadIcon, PlusIcon } from "lucide-react";

import { trpc } from "@blis/trpc/react";

import { buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { mutationResponse } from "@/utils/trpc";
import type { Org } from "../../types";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TableFilterRow } from "../TableFilterRow";

export function OrgsTable({ orgs }: { orgs: Org[] | undefined }) {
  const updateOrder = trpc.admin.org.updateOrder.useMutation(
    mutationResponse({
      errorTitle: "Error reordering clients",
      successMessage: "Clients reordered",
    }),
  );
  const { options, filterTextProps } = usePersistedAdminGridOptions(
    "orgs",
    orgs,
    [
      {
        field: "name",
        cellRenderer: (params) => (
          <div className="flex items-center gap-2">
            <Link
              href={{
                to: "/admin/clients/$id/edit",
                params: { id: params.id as string },
              }}
              className="block truncate"
            >
              {params.name}
            </Link>
          </div>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          name: params.data.name,
        }),
        rowDrag: true,
      },
      { field: "code" },
      { field: "orgType", filter: "agSetColumnFilter" },
      { field: "primarySelectable", filter: "agSetColumnFilter" },
      { field: "selectable", filter: "agSetColumnFilter" },
      { field: "ignoreInvoices", filter: "agSetColumnFilter" },
    ],
    {
      getRowId: (t) => t.data.id.toString(),
      onRowDragEnd: (e) => {
        const ids: number[] = [];
        e.api.forEachNode((node) => {
          if (node.data?.id) {
            ids.push(node.data.id);
          }
        });
        updateOrder.mutate({ orgs: ids });
      },
    },
  );

  return (
    <>
      <TableFilterRow inputProps={filterTextProps}>
        <div className="flex items-center gap-2">
          <a
            href="/api/export/orgs"
            className={buttonVariants({ variant: "outline" })}
            target="_blank"
            rel="noopener"
          >
            <DownloadIcon />
            Export
          </a>

          <Link
            href={{ to: "/admin/clients/new" }}
            className={buttonVariants()}
          >
            <PlusIcon />
            Add <span className="hidden sm:inline">&nbsp;Organization</span>
          </Link>
        </div>
      </TableFilterRow>
      <AdminGrid {...options} />
    </>
  );
}
